// @refresh reset — dev-only: force remount so the useMemo([]) board nodes hot-reload on edit (no prod effect)
import React, { useMemo } from "react";
import { type Node, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";

// ─────────────────────────────────────────────────────────────────────────────
// STARTUP BOARD — Sol's venture board for the Startup venture.
// Refactored to the LOCKED board-container standard (Erez-locked 2026-07-19):
// every logical group is ONE <SectionBox> (neutral-gray, self-hugging, its short
// label as the big black pill INSIDE), holding WHITE content cards. ALL section
// boxes live inside ONE <SectionGrid> in ONE React Flow node → a wide rectangle.
// NOTHING lives outside a SectionBox. Rendered through the shared <BoardCanvas>.
//
// 5 canonical zones (stakeholder operating model) + the business-plan deck:
//   The Plan · Business Plan (deck viewer) · Strategy · The Team · Execution · Decisions
// The Business-Plan section keeps the inline deck viewer with its "▶ Play"
// narrated-video button + prev/next arrows — preserved verbatim.
// ─────────────────────────────────────────────────────────────────────────────

const T = { bg: "#E5E5E5", dots: "#CFCFCF", ink: "#1C1917", ink2: "#57534E", line: "#EADDD0", white: "#FFFFFF" };
const FONT = "Inter, system-ui, -apple-system, sans-serif";
const PETROL = "#2B5560";
const AMBER = "#B8842B";
const WARM = "#B4552F";
const SAGE = "#5E7D5E";

// ── Types ────────────────────────────────────────────────────────────────────
type Stage = { n: string; name: string; detail: string; state: "done" | "now" | "next" };

// ── Helper: white content card (subtle border + shadow). The SectionBox black
// label names the section; a card carries only its real content (+ an optional
// small eyebrow tag), never a duplicate section title. ─────────────────────────

function Card({ tag, children, w = 680 }: { tag?: string; children: React.ReactNode; w?: number }) {
  return (
    <div style={{ fontFamily: FONT, width: w, background: T.white, border: `1px solid ${T.line}`, borderRadius: 16, boxShadow: "0 20px 50px rgba(0,0,0,0.10)", overflow: "hidden" }}>
      {tag && (
        <div style={{ padding: "16px 24px 12px", borderBottom: `1px solid ${T.line}` }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: PETROL, background: "rgba(43,85,96,0.08)", borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" }}>{tag}</span>
        </div>
      )}
      <div style={{ padding: "20px 24px 24px" }}>{children}</div>
    </div>
  );
}

// ── White cards for each zone ────────────────────────────────────────────────

// The Plan — the venture as one pipeline diagram.
function PlanDiagramCard({ data }: { data: { stages: Stage[] } }) {
  const stateStyle = (s: Stage["state"]) =>
    s === "done" ? { color: SAGE, bg: "rgba(94,125,94,0.12)", label: "DONE" }
      : s === "now" ? { color: AMBER, bg: "rgba(184,132,43,0.14)", label: "NOW" }
        : { color: "#9A8FA0", bg: "rgba(154,143,160,0.12)", label: "NEXT" };
  return (
    <Card tag="WHOLE PICTURE · WHERE WE ARE" w={1560}>
      {/* Sources */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 16, borderBottom: `1px solid ${T.line}`, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#9A8FA0", flexShrink: 0 }}>EVIDENCE IN</span>
        {[
          { t: "Isenberg 49/50", s: "242 problems, $0.04" },
          { t: "Hormozi corpus", s: "19 banked, queued" },
        ].map(src => (
          <div key={src.t} style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: "5px 10px", background: "#FDF7F2" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.ink }}>{src.t}</div>
            <div style={{ fontSize: 10.5, color: T.ink2 }}>{src.s}</div>
          </div>
        ))}
      </div>
      {/* Pipeline */}
      <div style={{ display: "flex", alignItems: "stretch", gap: 0, marginTop: 16 }}>
        {data.stages.map((st, i) => {
          const tone = stateStyle(st.state);
          return (
            <div key={st.n} style={{ display: "flex", alignItems: "stretch", flex: 1, minWidth: 0 }}>
              <div style={{ flex: 1, minWidth: 0, border: `1px solid ${st.state === "now" ? AMBER : T.line}`, background: st.state === "now" ? "rgba(184,132,43,0.05)" : T.white, borderRadius: 10, padding: "10px 10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#9A8FA0" }}>{st.n}</span>
                  <span style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: "0.08em", color: tone.color, background: tone.bg, borderRadius: 999, padding: "1px 6px" }}>{tone.label}</span>
                </div>
                <div style={{ marginTop: 4, fontSize: 13.5, fontWeight: 800, color: T.ink }}>{st.name}</div>
                <div style={{ marginTop: 3, fontSize: 10.5, color: T.ink2, lineHeight: 1.35 }}>{st.detail}</div>
              </div>
              {i < data.stages.length - 1 && <div style={{ display: "flex", alignItems: "center", padding: "0 3px", color: "#C9BBAD", fontSize: 14 }}>→</div>}
            </div>
          );
        })}
      </div>
      {/* Rules */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.line}`, fontSize: 11.5, color: T.ink2, lineHeight: 1.5 }}>
        <b>Rule:</b> Discovery only — no builds until Erez ratifies. <b>Engine:</b> Sol DeepSeek, team DeepSeek. <b>Cost:</b> $0.04 total.
      </div>
    </Card>
  );
}

// Strategy — thesis, market logic, money model, phase goal.
function StrategyCard() {
  return (
    <Card tag="THESIS">
      <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
        Sell the completed job — not the tool — to boring SMB verticals at $1–5K/mo
      </div>
      <div style={{ marginTop: 12, fontSize: 13.5, color: T.ink2, lineHeight: 1.55 }}>
        Agents are powerful but "Linux" — terminal-bound, raw. The mass market wants a managed AI employee: calls answered, bookings filled, follow-ups sent. Pay for outcomes, not dashboards.
      </div>
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${T.line}`, display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 24, rowGap: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#9A8FA0", marginBottom: 4 }}>MARKET</div>
          <div style={{ fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>$2–10M SMBs pay agencies $5–15K/mo for incomplete results. Buyers can't name use cases — the seller who maps vertical jobs-to-be-done wins.</div>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#9A8FA0", marginBottom: 4 }}>MONEY</div>
          <div style={{ fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>$1–5K/mo flat per client. 80%+ gross margin. ~$8/mo DeepSeek per agent. Total research: $0.04.</div>
        </div>
      </div>
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.line}` }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: AMBER }}>PHASE GOAL</span>
        <div style={{ marginTop: 4, fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>Ratify problem pick → Hormozi witness → C-suite. Evidence: 242 problems, 49/50 Isenberg, blind bake-off validated.</div>
      </div>
    </Card>
  );
}

// The Team — all 4 workers consolidated in one card.
function TeamCard() {
  const workers = [
    { initial: "D", name: "Dex-DS", role: "Research", assignment: "Awaiting Hormozi dispatch", next: "Pull Hormozi transcripts", color: "#5E7D5E" },
    { initial: "F", name: "Forge", role: "Builder", assignment: "Landscape survey done", next: "Await ratify → prototype", color: "#B8842B" },
    { initial: "N", name: "Nash", role: "GTM", assignment: "Positioning, pricing done", next: "Await ratify → refine", color: "#B4552F" },
    { initial: "V", name: "Vera", role: "Validation", assignment: "HELD — ask Jarvis to boot", next: "Landing pages + tests", color: "#9A8FA0" },
  ];
  return (
    <Card tag="4 WORKERS">
      {workers.map((w, i) => (
        <div key={w.name} style={{ display: "flex", gap: 12, padding: i > 0 ? "12px 0 0" : "0 0 0", borderTop: i > 0 ? `1px solid ${T.line}` : "none", marginTop: i > 0 ? 12 : 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: w.color, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 800, flexShrink: 0 }}>{w.initial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <span style={{ fontSize: 15, fontWeight: 800, color: T.ink }}>{w.name}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: PETROL }}>{w.role}</span>
            </div>
            <div style={{ marginTop: 3, fontSize: 12, color: T.ink2, lineHeight: 1.4 }}>
              <b>Now:</b> {w.assignment}<br /><b>Next:</b> {w.next}
            </div>
          </div>
        </div>
      ))}
    </Card>
  );
}

// Execution — shipped, in-flight, cost, run plan.
function ExecutionCard({ data }: { data: { steps: { step: string; state: "done" | "now" | "next" }[] } }) {
  const dot = (s: string) =>
    s === "done" ? { bg: SAGE, label: "DONE" }
      : s === "now" ? { bg: AMBER, label: "NOW" }
        : { bg: "#D6C7B8", label: "NEXT" };
  return (
    <Card tag="SHIPPED · IN FLIGHT">
      {/* Shipped */}
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: SAGE }}>SHIPPED</span>
      <div style={{ marginTop: 6, marginBottom: 14, display: "flex", flexDirection: "column", gap: 4 }}>
        {[
          "Isenberg 49/50 — 242 problems, $0.04",
          "Scored shortlist + blind bake-off audit",
          "/startup-board live, 5 canonical zones",
          "Team seated: landscape + GTM done",
        ].map(s => <div key={s} style={{ fontSize: 12, color: T.ink2, lineHeight: 1.4 }}>✓ {s}</div>)}
      </div>
      {/* In flight */}
      <div style={{ paddingTop: 12, borderTop: `1px solid ${T.line}` }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: AMBER }}>IN FLIGHT</span>
        <div style={{ marginTop: 4, fontSize: 12, color: T.ink2, lineHeight: 1.5 }}>Erez ratifies problem pick (phase-gate). Board consolidation into portrait pages. Pitch deck authoring.</div>
      </div>
      {/* Cost */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.line}` }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#9A8FA0" }}>COST</span>
        <div style={{ marginTop: 4, display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 16, rowGap: 3, fontSize: 11.5, color: T.ink2 }}>
          <span>DeepSeek (Isenberg): <b style={{ color: T.ink }}>$0.04</b></span>
          <span>Sol engine: <b style={{ color: T.ink }}>$200/mo flat</b></span>
          <span>Team compute: <b style={{ color: T.ink }}>~$0.01/hr</b></span>
        </div>
      </div>
      {/* Run plan */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.line}` }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#9A8FA0", marginBottom: 8, display: "block" }}>RUN PLAN</span>
        {data.steps.map((r, i) => {
          const d = dot(r.state);
          return (
            <div key={r.step} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: d.bg, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: r.state === "now" ? 700 : 400, color: r.state === "now" ? AMBER : T.ink2 }}>{r.step}</span>
              <span style={{ fontSize: 8.5, fontWeight: 800, color: "#fff", background: d.bg, borderRadius: 999, padding: "1px 6px" }}>{d.label}</span>
              {i < data.steps.length - 1 && <span style={{ color: "#C9BBAD", fontSize: 11, marginLeft: "auto" }}>↓</span>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// Decisions — open asks with options + recommendation, and a decided log.
function DecisionsCard() {
  return (
    <Card tag="1 OPEN · AWAITING OWNERS">
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: AMBER }}>OPEN</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: WARM, background: "rgba(180,85,47,0.10)", borderRadius: 999, padding: "2px 8px" }}>REQUIRES EREZ</span>
        </div>
        <div style={{ marginTop: 10, fontSize: 15, fontWeight: 800, color: T.ink }}>Ratify the problem pick</div>
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 7 }}>
          {[
            { label: "Option A (recommended)", desc: "Agent-SaaS for boring SMB verticals. $1-5K/mo managed AI employees. 4.6/5. Both analysts picked #1. Start with one vertical.", tag: "RECOMMENDED" },
            { label: "Option B", desc: "Agent reliability infra. 4.2/5. Developer GTM requires different playbook.", tag: "" },
            { label: "Option C", desc: "Neither — run Hormozi corpus first for second witness.", tag: "" },
          ].map(o => (
            <div key={o.label} style={{ border: `1px solid ${T.line}`, borderRadius: 8, padding: "8px 12px", background: o.tag ? "rgba(94,125,94,0.06)" : "#FDF7F2" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: o.tag ? SAGE : T.ink }}>{o.label}</span>
                {o.tag && <span style={{ fontSize: 8, fontWeight: 800, color: "#fff", background: SAGE, borderRadius: 999, padding: "1px 6px" }}>{o.tag}</span>}
              </div>
              <div style={{ marginTop: 3, fontSize: 11.5, color: T.ink2, lineHeight: 1.45 }}>{o.desc}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Decided */}
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.line}` }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#9A8FA0" }}>DECIDED</span>
        {["Research budget approved ($0.04)", "Bake-off format (blind, both engines)", "Board law (5 zones, portrait pages)", "Team structure (Sol CEO + 4 agents)"].map(d => (
          <div key={d} style={{ fontSize: 11.5, color: T.ink2, marginTop: 3 }}>✓ {d}</div>
        ))}
      </div>
    </Card>
  );
}

// ── Inline deck viewer — renders business plan slides inside the board ──
// PRESERVED VERBATIM: slides defined inline, arrows flip pages, and the "▶ Play"
// button launches the narrated video (on R2). Erez flips through the deck without
// ever leaving /startup-board.

const DECK_SLIDES = [
  { title: "The Startup Venture", sub: "Managed AI employees for boring SMB verticals", body: "Business plan presented to the owners. Discovery complete. Shortlist scored. Team seated. Sol, CEO. July 2026." },
  { title: "Isenberg corpus: 49/50 videos", sub: "MARKET RESEARCH", body: "242 problem extractions. Blind bake-off validated. Cost: $0.04 DeepSeek.", bullets: [
    "#1 Agent-SaaS for SMB verticals — 4.6/5. 16/49 videos. Both analysts picked it.",
    "#2 Agent reliability infra — 4.2/5. Loudest operational pain. Everyone hand-rolls watchdogs.",
    "#3 Computer-use agents — 4.0/5. Legacy software unlock. $30B RPA market.",
    "#4-7 tied at 3.6 — distribution, autonomous agents, AI-OS, trust wrappers.",
  ]},
  { title: "Hormozi corpus: 19 banked videos", sub: "MARKET RESEARCH", body: "Second witness. Lens: business economics, not tech patterns. Awaiting extraction.", bullets: [
    "Creator-risk continuum — AI eats low-proof content. Proof-density is the moat.",
    "Service-first, productize later — start as outcome delivery, standardize over time.",
    "High-ticket to established businesses — $2-10M companies, not startups.",
    "Distribution via content + community — built-in, not paid.",
  ]},
  { title: "Who is in the agent-SaaS lane", sub: "COMPETITION", body: "Emerging but unconsolidated. No single player owns the vertical-SMB managed-agent segment.", bullets: [
    "OpenClaw/Claudebot — consumer framework, no service layer.",
    "Orgo — VMs for agents. SMBs don’t buy VMs.",
    "ServiceTitan, Housecall Pro — CRM tools, no AI employee layer.",
    "Boutique agencies — $5-15K/mo for incomplete results.",
  ]},
  { title: "Our edge", sub: "COMPETITION", body: "Vertical specificity plus agent deployment.", bullets: [
    "Completed job — calls answered, bookings filled, follow-ups sent.",
    "One vertical at a time — deep, not wide. Workflow compounds.",
    "$1-5K/mo flat — no per-seat, no usage cap. 80%+ margin.",
    "Zero setup — connect phone and calendar, go.",
    "Model-agnostic — switch engines via env var.",
  ]},
  { title: "Money model", sub: "FINANCE", body: "Simple, transparent, high-margin. One price, one vertical at a time.", bullets: [
    "Price: $1-5K/mo flat. COGS: ~$30/mo per client. 80%+ margin.",
    "Pilot: 3-5 clients at $2K/mo = $6-10K MRR proof point.",
    "Then expand within vertical, then new verticals. Workflow library compounds.",
    "Long-term: vertical SaaS add-on + marketplace referral fees.",
  ]},
  { title: "30-day validation ($500 budget)", sub: "FIRST STEPS", body: "Vera handles these when booted. No code until landings confirm demand.", bullets: [
    "1. Landing page: ‘$2K/mo. First month free.’ One vertical.",
    "2. 100-200 visitors via Nextdoor, Facebook groups, cold DMs.",
    "3. Measure waitlist conversion. Target: 5%+ = validation signal.",
    "4. If positive: Forge builds MVP in 2 weeks (OpenClaw + Twilio).",
    "5. 3 design partners at $1K/mo. Measure call recovery + booking lift.",
  ]},
  { title: "Risk & mitigation", sub: "RISK", body: "Four real risks, each with a specific mitigation.", bullets: [
    "SMB trust? Free first month + human backup. All calls reviewable.",
    "Slow scaling? Start deep in roofing ($30B TAM, fragmented).",
    "Platform lock-in? Model-agnostic. OpenClaw is MIT, runnable anywhere.",
    "Cloned by competitors? 6-12 month head start on vertical workflows.",
  ]},
  { title: "Gate-1: Ratify the problem pick", sub: "THE ASK", body: "One decision opens the build phase. Option A on the board.", bullets: [
    "Agent-SaaS for boring SMB verticals. 4.6/5. Both analysts picked #1.",
    "After: boot Vera, dispatch Forge for MVP, run Hormozi in parallel.",
    "Total spend to date: $0.04 across the entire discovery phase.",
  ]},
  { title: "Over to you, owners", sub: "CLOSE", body: "The board has three options in the Decisions zone with Sol’s recommendation at Option A.", bullets: [] },
];

const VIDEO_URL = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/videos/startup-business-plan.mp4";

function InlineDeckViewerCard() {
  const [slide, setSlide] = React.useState(0);
  const [showVideo, setShowVideo] = React.useState(false);
  const s = DECK_SLIDES[slide];
  return (
    <>
      {/* Video overlay modal — fixed position, outside React Flow transform */}
      {showVideo && (
        <div
          className="nopan nodrag"
          onClick={() => setShowVideo(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(20,17,15,0.82)", display: "flex", alignItems: "center", justifyContent: "center", padding: 40, pointerEvents: "auto" }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ position: "relative", width: "min(1200px, 94%)", borderRadius: 18, overflow: "hidden", background: "#000", boxShadow: "0 40px 120px rgba(0,0,0,0.5)" }}
          >
            <button
              onClick={() => setShowVideo(false)}
              style={{ position: "absolute", top: 12, right: 12, zIndex: 10, cursor: "pointer", fontFamily: FONT, width: 38, height: 38, borderRadius: "50%", border: "none", background: "rgba(28,25,23,0.7)", color: "#fff", fontSize: 22, display: "flex", alignItems: "center", justifyContent: "center" }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              ×
            </button>
            <video src={VIDEO_URL} controls autoPlay style={{ display: "block", width: "100%", maxHeight: "85vh", background: "#000" }} />
          </div>
        </div>
      )}

      <div className="nopan nodrag" style={{ fontFamily: FONT, width: 720, background: T.white, border: `2px solid ${PETROL}`, borderRadius: 18, boxShadow: "0 20px 50px rgba(0,0,0,0.12)", overflow: "hidden" }}>
        {/* Header bar */}
        <div style={{ padding: "12px 20px", background: PETROL, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "#fff" }}>BUSINESS PLAN · {s.sub}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.7)" }}>{slide + 1} / {DECK_SLIDES.length}</span>
        </div>

        {/* Slide content */}
        <div style={{ padding: 28, minHeight: 340, display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.2 }}>{s.title}</div>
          {s.body && <div style={{ marginTop: 10, fontSize: 13.5, color: T.ink2, lineHeight: 1.55 }}>{s.body}</div>}
          {s.bullets && s.bullets.length > 0 && (
            <ul style={{ marginTop: 14, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
              {s.bullets.map((b, i) => <li key={i} style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>{b}</li>)}
            </ul>
          )}
        </div>

        {/* Controls */}
        <div style={{ padding: "10px 20px 14px", borderTop: `1px solid ${T.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6 }}>
            <button style={{ pointerEvents: "auto", cursor: "pointer", fontFamily: FONT, border: `1px solid ${T.line}`, borderRadius: 8, background: T.white, padding: "6px 14px", fontSize: 13, fontWeight: 600, color: T.ink, opacity: slide === 0 ? 0.4 : 1 }} disabled={slide === 0} onClick={() => setSlide(Math.max(0, slide - 1))} onPointerDown={(e) => e.stopPropagation()}>← Prev</button>
            <button style={{ pointerEvents: "auto", cursor: "pointer", fontFamily: FONT, border: `1px solid ${T.line}`, borderRadius: 8, background: T.white, padding: "6px 14px", fontSize: 13, fontWeight: 600, color: T.ink, opacity: slide === DECK_SLIDES.length - 1 ? 0.4 : 1 }} disabled={slide === DECK_SLIDES.length - 1} onClick={() => setSlide(Math.min(DECK_SLIDES.length - 1, slide + 1))} onPointerDown={(e) => e.stopPropagation()}>Next →</button>
            <button style={{ pointerEvents: "auto", cursor: "pointer", fontFamily: FONT, border: `1px solid ${AMBER}`, borderRadius: 8, background: AMBER, padding: "6px 14px", fontSize: 13, fontWeight: 700, color: "#fff" }} onClick={() => setShowVideo(true)} onPointerDown={(e) => e.stopPropagation()}>▶ Play</button>
          </div>
          <a href="/pitch-doc/startup/business-plan" target="_blank" rel="noopener noreferrer" style={{ pointerEvents: "auto", fontFamily: FONT, fontSize: 12, fontWeight: 700, color: PETROL, textDecoration: "none" }} onPointerDown={(e) => e.stopPropagation()}>Full deck ↗</a>
        </div>
      </div>
    </>
  );
}

// ── Section standard — every group is ONE <SectionBox>; all boxes in ONE grid ──

type SecItem = { type: string; data?: any };

function renderSecItem(it: SecItem, key: number) {
  switch (it.type) {
    case "plan": return <PlanDiagramCard key={key} data={it.data} />;
    case "deck": return <InlineDeckViewerCard key={key} />;
    case "strategy": return <StrategyCard key={key} />;
    case "team": return <TeamCard key={key} />;
    case "execution": return <ExecutionCard key={key} data={it.data} />;
    case "decisions": return <DecisionsCard key={key} />;
    default: return null;
  }
}

function renderSection(s: { id: string; title: string; rows: SecItem[][] }) {
  // No tall sections: many rows → lay them in a grid so the box reads wider than tall.
  const n = s.rows.length;
  const cols = n > 6 ? 3 : n > 3 ? 2 : 1;
  return (
    <SectionBox key={s.id} title={s.title} layout="custom">
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, max-content)`, columnGap: 100, rowGap: 72, alignItems: "start" }}>
        {s.rows.map((row, ri) => (
          <div key={ri} style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 64 }}>
            {row.map((it, ci) => renderSecItem(it, ci))}
          </div>
        ))}
      </div>
    </SectionBox>
  );
}

// The whole board is ONE node: a SectionGrid packing all section boxes into a
// compact rectangle (columns fill top-down, then across).
function GridNode({ data }: { data: { defs: { id: string; title: string; rows: SecItem[][] }[] } }) {
  return (
    <SectionGrid>
      {data.defs.map(renderSection)}
    </SectionGrid>
  );
}

const nodeTypes: NodeTypes = {
  grid: GridNode as unknown as NodeTypes[string],
};

// ── Board content ────────────────────────────────────────────────────────────

function buildNodes(): Node[] {
  const stages: Stage[] = [
    { n: "01", name: "Ingest", detail: "Isenberg 49/50 · Hormozi banked", state: "done" },
    { n: "02", name: "Extract", detail: "242 problems across 49 videos", state: "done" },
    { n: "03", name: "Cluster", detail: "8 merged themes from 3 batches", state: "done" },
    { n: "04", name: "Score", detail: "Five axes · bake-off audited", state: "done" },
    { n: "05", name: "Shortlist", detail: "Sol pick: Agent-SaaS (4.6/5)", state: "done" },
    { n: "06", name: "Decide", detail: "Erez ratifies — awaiting owners", state: "now" },
    { n: "07", name: "Build", detail: "C-suite after the pick", state: "next" },
  ];

  const runSteps = [
    { step: "Isenberg 49/50", state: "done" as const },
    { step: "Cluster score", state: "done" as const },
    { step: "Shortlist", state: "done" as const },
    { step: "Bake-off fix", state: "done" as const },
    { step: "Team brief", state: "done" as const },
    { step: "Ratify pick", state: "now" as const },
    { step: "Hormozi", state: "next" as const },
    { step: "Pitch deck", state: "next" as const },
    { step: "Build", state: "next" as const },
  ];

  const defs: { id: string; title: string; rows: SecItem[][] }[] = [
    { id: "sec-plan", title: "The Plan", rows: [[{ type: "plan", data: { stages } }]] },
    { id: "sec-business-plan", title: "Business Plan", rows: [[{ type: "deck" }]] },
    { id: "sec-strategy", title: "Strategy", rows: [[{ type: "strategy" }]] },
    { id: "sec-team", title: "The Team", rows: [[{ type: "team" }]] },
    { id: "sec-execution", title: "Execution", rows: [[{ type: "execution", data: { steps: runSteps } }]] },
    { id: "sec-decisions", title: "Decisions", rows: [[{ type: "decisions" }]] },
  ];

  return [{ id: "board-grid", type: "grid", position: { x: 0, y: 0 }, draggable: false, data: { defs } }];
}

// ── Page — rendered through the shared BoardCanvas ───────────────────────────

export function StartupBoardPage() {
  const nodes = useMemo<Node[]>(() => buildNodes(), []);
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} nodeTypes={nodeTypes} fitViewPadding={0.12} />
    </div>
  );
}

export default StartupBoardPage;
